import * as React from 'react';
import { DialogContentText, Tooltip, IconButton } from '@mui/material';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AppContext } from '../../App';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import useGlynt from '../hooks/useGlynt';

export interface PromoteToParentActionProps {
  documentId: string;
  // The DocumentData id to promote. The manifest projects this directly
  // (advanced=true), so callers pass it in — no lookup needed.
  documentDataId: string;
}

interface PromoteErrorBody {
  error?: string;
  code?: 'REPARENTING_ERROR' | 'INVALID_TRANSITION' | 'MISSING_REQUIRED_FIELDS' | string;
}

const errorMessageForCode = (code: string | undefined, fallback: string): string => {
  switch (code) {
    case 'REPARENTING_ERROR':
      return 'This document is no longer a duplicate.';
    case 'INVALID_TRANSITION':
      return "Can't promote from the current state.";
    case 'MISSING_REQUIRED_FIELDS':
      return 'Cannot promote: a required field is missing on this document.';
    default:
      return fallback;
  }
};

const PromoteToParentAction: React.FC<PromoteToParentActionProps> = ({
  documentId,
  documentDataId,
}) => {
  const [open, setOpen] = React.useState(false);
  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  const promoteMutation = useMutation({
    mutationFn: async () => {
      const api = await glynt;
      return api.documentData.promoteToParent(documentDataId);
    },
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Document promoted to parent.',
      });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error: any) => {
      const body: PromoteErrorBody | undefined = error?.response?.data;
      const message = errorMessageForCode(
        body?.code,
        body?.error || error?.message || 'Failed to promote document.'
      );
      setToast({ variant: 'error', open: true, message });
      // On REPARENTING_ERROR the row is stale — refresh so the icon disappears.
      if (body?.code === 'REPARENTING_ERROR') {
        queryClient.invalidateQueries({ queryKey: ['documents'] });
      }
    },
  });

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    if (promoteMutation.isPending) return;
    setOpen(false);
  };

  const handleConfirm = () => {
    promoteMutation.mutate(undefined, {
      onSettled: () => setOpen(false),
    });
  };

  return (
    <>
      <Tooltip
        placement="top"
        title="Promote to parent"
        disableFocusListener
        disableTouchListener
      >
        <span>
          <IconButton
            size="small"
            data-cy={`btn-promote-to-parent-${documentId}`}
            onClick={handleOpen}
            disabled={promoteMutation.isPending}
            sx={{
              p: 0,
              color: '#66BBA9',
              '&:hover': { backgroundColor: 'rgba(102, 187, 169, 0.12)' },
            }}
          >
            <KeyboardDoubleArrowUpIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </span>
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleClose}
        loading={promoteMutation.isPending}
        cypressId="dialog-promote-to-parent"
      >
        <DialogTitleGlynt
          title="Promote to parent"
          closeModal={handleClose}
          loading={promoteMutation.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>
            This will make this document the canonical (kept) document of its duplicate
            cluster. The current parent will be demoted to a duplicate, and every sibling
            will be re-pointed onto this document. Continue?
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleConfirm}
          closeModal={handleClose}
          cancelCypressId="btn-promote-to-parent-cancel"
          submitCypressId="btn-promote-to-parent-confirm"
          clearCypressId=""
          submitButtonText="Promote"
          submitLoading={promoteMutation.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default PromoteToParentAction;
